import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Save, 
  Plus, 
  Edit, 
  Trash2, 
  MapPin, 
  Clock, 
  DollarSign,
  Users,
  Briefcase,
  Calendar
} from 'lucide-react'
import { useFirestore } from '@hooks/useFirestore'

const CareersManager = () => {
  const careersFirestore = useFirestore('careers')
  const [editingCareer, setEditingCareer] = useState(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  // No default careers data - start with empty collection

  const clearAllCareers = async () => {
    if (confirm('Are you sure you want to delete ALL career postings? This action cannot be undone.')) {
      try {
        for (const career of careersFirestore.data) {
          await careersFirestore.deleteItem(career.id)
        }
        setMessage('All career postings have been cleared!')
        setTimeout(() => setMessage(''), 3000)
      } catch (error) {
        setMessage('Error clearing careers: ' + error.message)
      }
    }
  }

  const handleEdit = (career) => {
    setEditingCareer({ ...career })
    setIsAddingNew(false)
    setMessage('')
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage('')

    try {
      if (editingCareer) {
        if (isAddingNew) {
          const newCareer = {
            ...editingCareer,
            id: editingCareer.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            postedDate: new Date().toISOString().split('T')[0],
            applications: 0
          }
          await careersFirestore.addItem(newCareer)
          setMessage('Career posting added successfully!')
        } else {
          await careersFirestore.updateItem(editingCareer.id, editingCareer)
          setMessage('Career posting updated successfully!')
        }
        setEditingCareer(null)
        setIsAddingNew(false)
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000)
      }
    } catch (error) {
      setMessage('Error saving career posting: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleAddNew = () => {
    setEditingCareer({
      id: null,
      title: '',
      department: 'Consulting',
      location: '',
      type: 'Full-time',
      experience: '',
      salary: '',
      description: '',
      requirements: [''],
      responsibilities: [''],
      benefits: [''],
      status: 'draft',
      postedDate: new Date().toISOString().split('T')[0],
      applications: 0
    })
    setIsAddingNew(true)
    setMessage('')
  }

  const handleDelete = async (careerId) => {
    if (confirm('Are you sure you want to delete this career posting?')) {
      try {
        await careersFirestore.deleteItem(careerId)
        setMessage('Career posting deleted successfully!')
        setTimeout(() => setMessage(''), 3000)
      } catch (error) {
        setMessage('Error deleting career posting: ' + error.message)
      }
    }
  }

  const updateEditingCareer = (field, value) => {
    setEditingCareer(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const updateArrayField = (field, index, value) => {
    setEditingCareer(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  const addArrayItem = (field) => {
    setEditingCareer(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setEditingCareer(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'closed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (careersFirestore.loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-navy-200 border-t-navy-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted">Loading careers...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-display-3 text-navy-900 mb-2">Careers Management</h1>
          <p className="text-body text-muted">Manage job postings and career opportunities.</p>
        </div>
        <div className="flex space-x-4">
          <button onClick={handleAddNew} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add New Position
          </button>
          {careersFirestore.data.length > 0 && (
            <button onClick={clearAllCareers} className="btn-secondary text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-xl ${
          message.includes('Error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          <div className="flex items-center space-x-2">
            <span>{message}</span>
          </div>
        </div>
      )}

      {editingCareer ? (
        <div className="card p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-heading-2 text-navy-900">
              {isAddingNew ? 'Add New Position' : 'Edit Position'}
            </h3>
            <div className="flex space-x-4">
              <button 
                onClick={handleSave} 
                className="btn-primary"
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button 
                onClick={() => {
                  setEditingCareer(null)
                  setMessage('')
                }} 
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-navy-900 mb-2">Job Title</label>
                <input
                  type="text"
                  value={editingCareer.title}
                  onChange={(e) => updateEditingCareer('title', e.target.value)}
                  className="form-input"
                  placeholder="Enter job title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">Department</label>
                  <select
                    value={editingCareer.department}
                    onChange={(e) => updateEditingCareer('department', e.target.value)}
                    className="form-input"
                  >
                    <option value="Consulting">Consulting</option>
                    <option value="Analytics">Analytics</option>
                    <option value="Operations">Operations</option>
                    <option value="Business Development">Business Development</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">Employment Type</label>
                  <select
                    value={editingCareer.type}
                    onChange={(e) => updateEditingCareer('type', e.target.value)}
                    className="form-input"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">Location</label>
                  <input
                    type="text"
                    value={editingCareer.location}
                    onChange={(e) => updateEditingCareer('location', e.target.value)}
                    className="form-input"
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">Experience Required</label>
                  <input
                    type="text"
                    value={editingCareer.experience}
                    onChange={(e) => updateEditingCareer('experience', e.target.value)}
                    className="form-input"
                    placeholder="e.g., 3-5 years"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy-900 mb-2">Salary Range</label>
                <input
                  type="text"
                  value={editingCareer.salary}
                  onChange={(e) => updateEditingCareer('salary', e.target.value)}
                  className="form-input"
                  placeholder="e.g., $80,000 - $120,000"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy-900 mb-2">Job Description</label>
                <textarea
                  value={editingCareer.description}
                  onChange={(e) => updateEditingCareer('description', e.target.value)}
                  className="form-textarea"
                  rows={4}
                  placeholder="Enter job description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy-900 mb-2">Status</label>
                <select
                  value={editingCareer.status}
                  onChange={(e) => updateEditingCareer('status', e.target.value)}
                  className="form-input"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="space-y-6">
              {['requirements', 'responsibilities', 'benefits'].map((field) => (
                <div key={field}>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-semibold text-navy-900 capitalize">
                      {field}
                    </label>
                    <button 
                      onClick={() => addArrayItem(field)} 
                      className="text-sm text-navy-700 hover:text-navy-900"
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Add {field.slice(0, -1)}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {editingCareer[field].map((item, index) => (
                      <div key={index} className="flex space-x-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => updateArrayField(field, index, e.target.value)}
                          className="form-input flex-1"
                          placeholder={`Enter ${field.slice(0, -1)}`}
                        />
                        <button
                          onClick={() => removeArrayItem(field, index)}
                          className="p-2 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {careersFirestore.data.map((career, index) => (
          <motion.div
            key={career.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="card p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-heading-2 text-navy-900 mb-2">{career.title}</h3>
                <p className="text-body-small text-muted mb-3">{career.department}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(career.status)}`}>
                {career.status}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2 text-sm text-neutral-600">
                <MapPin className="w-4 h-4" />
                <span>{career.location}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-neutral-600">
                <Clock className="w-4 h-4" />
                <span>{career.type} • {career.experience}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-neutral-600">
                <DollarSign className="w-4 h-4" />
                <span>{career.salary}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-neutral-600">
                <Users className="w-4 h-4" />
                <span>{career.applications} applications</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-neutral-600">
                <Calendar className="w-4 h-4" />
                <span>Posted {new Date(career.postedDate).toLocaleDateString()}</span>
              </div>
            </div>

            <p className="text-body-small text-muted mb-6 line-clamp-3">{career.description}</p>

            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(career)}
                className="flex-1 btn-secondary text-sm py-2"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDelete(career.id)}
                className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

export default CareersManager 